package ir.alirezaivaz.finglish.ui.sheets

import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.ExperimentalMaterial3Api
import androidx.compose.material3.HorizontalDivider
import androidx.compose.material3.Icon
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.ModalBottomSheet
import androidx.compose.material3.SegmentedButton
import androidx.compose.material3.SegmentedButtonDefaults
import androidx.compose.material3.SingleChoiceSegmentedButtonRow
import androidx.compose.material3.Text
import androidx.compose.material3.rememberModalBottomSheetState
import androidx.compose.runtime.Composable
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableIntStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.text.style.TextAlign
import finglish.composeapp.generated.resources.Res
import finglish.composeapp.generated.resources.action_settings
import finglish.composeapp.generated.resources.app_name
import finglish.composeapp.generated.resources.settings_app_version
import finglish.composeapp.generated.resources.settings_dynamic_colors
import finglish.composeapp.generated.resources.settings_language
import finglish.composeapp.generated.resources.settings_theme
import ir.alirezaivaz.finglish.dto.Language
import ir.alirezaivaz.finglish.dto.Language.Companion.typography
import ir.alirezaivaz.finglish.dto.Theme
import ir.alirezaivaz.finglish.ui.theme.Dimens
import ir.alirezaivaz.finglish.ui.widgets.SwitchItem
import ir.alirezaivaz.finglish.utils.SettingsManager
import ir.alirezaivaz.finglish.utils.Utils
import ir.alirezaivaz.kartam.extensions.handPointerIcon
import org.jetbrains.compose.resources.painterResource
import org.jetbrains.compose.resources.stringResource

@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun SettingsSheet(
    onDismissRequest: () -> Unit,
    onThemeChangedRequest: (theme: Theme) -> Unit,
    onLanguageChangedRequest: (Language) -> Unit
) {
    val sheetState = rememberModalBottomSheetState(
        skipPartiallyExpanded = true
    )

    ModalBottomSheet(
        sheetState = sheetState,
        onDismissRequest = onDismissRequest
    ) {
        SettingsSheetContent(
            onDismissRequest = onDismissRequest,
            onThemeChangedRequest = onThemeChangedRequest,
            onLanguageChangedRequest = onLanguageChangedRequest
        )
    }
}

@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun SettingsSheetContent(
    onDismissRequest: () -> Unit,
    onThemeChangedRequest: (theme: Theme) -> Unit,
    onLanguageChangedRequest: (language: Language) -> Unit
) {
    val theme by SettingsManager.theme.collectAsState()
    val language by SettingsManager.language.collectAsState()
    val themes = Theme.entries.map { it }
    val languages = Language.entries.map { it }
    var selectedThemeIndex by remember { mutableIntStateOf(themes.indexOf(theme)) }
    var selectedLanguageIndex by remember { mutableIntStateOf(languages.indexOf(language)) }
    val isDynamicColors by SettingsManager.isDynamicColors.collectAsState()

    Column(
        modifier = Modifier.verticalScroll(rememberScrollState()),
        horizontalAlignment = Alignment.CenterHorizontally
    ) {
        Text(
            text = stringResource(Res.string.action_settings),
            modifier = Modifier
                .fillMaxWidth()
                .padding(horizontal = Dimens.large),
            textAlign = TextAlign.Center,
            style = MaterialTheme.typography.titleLarge,
        )
        Spacer(Modifier.height(Dimens.large))
        Text(
            text = stringResource(Res.string.settings_theme),
            style = MaterialTheme.typography.titleMedium,
            textAlign = TextAlign.Start,
            modifier = Modifier
                .fillMaxWidth()
                .padding(horizontal = Dimens.large)
        )
        Spacer(Modifier.height(Dimens.small))
        SingleChoiceSegmentedButtonRow(
            modifier = Modifier
                .fillMaxWidth()
                .padding(horizontal = Dimens.large),
        ) {
            themes.filter { it.isAvailable }.forEachIndexed { index, item ->
                SegmentedButton(
                    modifier = Modifier.handPointerIcon(),
                    selected = index == selectedThemeIndex,
                    icon = {
                        Icon(
                            painter = painterResource(
                                if (index == selectedThemeIndex) {
                                    item.iconFilled
                                } else {
                                    item.icon
                                }
                            ),
                            contentDescription = stringResource(item.title)
                        )
                    },
                    shape = SegmentedButtonDefaults.itemShape(
                        index = index,
                        count = themes.size
                    ),
                    onClick = {
                        selectedThemeIndex = index
                        onDismissRequest()
                        onThemeChangedRequest(item)
                    },
                    label = {
                        Text(
                            text = stringResource(item.title)
                        )
                    }
                )
            }
        }
        Spacer(Modifier.height(Dimens.small))
        Text(
            text = stringResource(Res.string.settings_language),
            style = MaterialTheme.typography.titleMedium,
            textAlign = TextAlign.Start,
            modifier = Modifier
                .fillMaxWidth()
                .padding(horizontal = Dimens.large)
        )
        Spacer(Modifier.height(Dimens.small))
        SingleChoiceSegmentedButtonRow(
            modifier = Modifier
                .fillMaxWidth()
                .padding(horizontal = Dimens.large),
        ) {
            languages.forEachIndexed { index, item ->
                SegmentedButton(
                    modifier = Modifier.handPointerIcon(),
                    selected = index == selectedLanguageIndex,
                    shape = SegmentedButtonDefaults.itemShape(
                        index = index,
                        count = languages.size
                    ),
                    onClick = {
                        selectedLanguageIndex = index
                        onDismissRequest()
                        onLanguageChangedRequest(item)
                    },
                    label = {
                        Text(
                            text = stringResource(item.title),
                            fontFamily = item.typography.bodyMedium.fontFamily
                        )
                    }
                )
            }
        }
        Spacer(Modifier.height(Dimens.small))
        if (Utils.isDynamicColorsSupported()) {
            SwitchItem(
                title = stringResource(Res.string.settings_dynamic_colors),
                isChecked = isDynamicColors,
                onCheckedChanged = {
                    SettingsManager.updateIsDynamicColors(it)
                }
            )
        }
        Spacer(Modifier.height(Dimens.large))
        HorizontalDivider(Modifier.padding(horizontal = Dimens.large))
        Spacer(Modifier.height(Dimens.large))
        Text(
            text = stringResource(
                Res.string.settings_app_version,
                stringResource(Res.string.app_name),
                Utils.getVersionName()
            ),
            textAlign = TextAlign.Center,
            style = MaterialTheme.typography.labelMedium,
            modifier = Modifier
                .fillMaxWidth()
                .padding(horizontal = Dimens.large),
        )
        Spacer(Modifier.height(Dimens.extraLarge))
    }
}
